import React, { useContext } from 'react';
import { Button } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { TimeEntries, TimeEntry } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';
import {
  sortBooleanAscending,
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  timeEntries: TimeEntries;
};

const TimeEntryTable = ({ timeEntries }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket, onAction } = useContext(TicketContext);
  const getTask = (taskId: string): string => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.tasks)
    ) {
      for (const task of ticket.ticket.tasks) {
        if (task.id === taskId) {
          return `${task.number}: ${task.name}`;
        }
      }
    }
    return '';
  };

  const getResource = (resourceId: string): string => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      for (const partyInvolved of ticket.ticket.partiesInvolved) {
        if (partyInvolved.id === resourceId) {
          return `${partyInvolved.name}`;
        }
      }
    }
    return '';
  };

  const getResourceEmail = (resourceId: string): string => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      for (const partyInvolved of ticket.ticket.partiesInvolved) {
        if (partyInvolved.id === resourceId) {
          return `${partyInvolved.email}`;
        }
      }
    }
    return '';
  };

  const columns: ColumnProps<TimeEntry>[] = [
    {
      title: 'Task',
      dataIndex: 'taskId',
      key: 'taskId',
      render: (_, record: TimeEntry) => getTask(record.taskId),
    },
    {
      title: 'Resource',
      dataIndex: 'resourceId',
      key: 'resourceId',
      render: (_, record: TimeEntry) => getResource(record.resourceId),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (_, record: TimeEntry) => record.description,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (_, record: TimeEntry) => formatDateOnly(record.date),
      sorter: (compA, compB) => sortStringAscending(compA.date, compB.date),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Hours',
      dataIndex: 'hoursLogged',
      key: 'hoursLogged',
      align: 'right',
      render: (_, record: TimeEntry) =>
        record.billable && record.billingId === '' ? (
          <span style={{ color: 'red' }}>
            <Hours amount={record.hoursLogged} />
          </span>
        ) : (
          <span>
            <Hours amount={record.hoursLogged} />
          </span>
        ),
      sorter: (compA, compB) =>
        sortNumberAscending(compA.hoursLogged, compB.hoursLogged),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Billable',
      dataIndex: 'billable',
      key: 'billable',
      render: (_, record: TimeEntry) =>
        record.billable ? (
          record.billingId === '' ? (
            <span style={{ color: 'red' }}>Yes</span>
          ) : (
            <span style={{ color: 'green' }}>Yes</span>
          )
        ) : (
          <span>No</span>
        ),
      sorter: (compA, compB) =>
        sortBooleanAscending(compA.billable, compB.billable),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Billed',
      dataIndex: 'billingId',
      key: 'billingId',
      render: (_, record: TimeEntry) =>
        !record.billable ? (
          <span>-</span>
        ) : record.billingId !== '' ? (
          <span>Yes - {record.billingId}</span>
        ) : (
          <span style={{ color: 'red' }}>No</span>
        ),
      sorter: (compA, compB) =>
        sortBooleanAscending(compA.billingId !== '', compB.billingId !== ''),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: TimeEntry) => {
        if (
          record.billingId === '' &&
          userProfile.email === getResourceEmail(record.resourceId)
        ) {
          return (
            <Button
              shape={'circle'}
              onClick={() => {
                onAction('show-time-entry-edit', JSON.stringify(record));
              }}
              icon={<IBDIcon name={'Edit'} />}
            />
          );
        }

        return <span />;
      },
    },
  ];

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(timeEntries) ? timeEntries : []}
        loading={false}
        rowKey="id"
      />
    </>
  );
};

export default TimeEntryTable;
